import _ from 'lodash';
import React, { createContext, useContext, useEffect, useMemo, useReducer } from 'react';
import { gEnums } from '../../enums/globalEnums';
import { swiperHandlers, swiperInitialState, swiperReducer } from '../reducers/SwiperReducer';
import { FilterContext } from './FilterContext';
import { FrameworkContext } from './FrameworkContent';
import { PageAreaContext } from './PageAreaContext';
import { ParentContext } from './ParentContext';
import { PreviewContext } from './PreviewContext';
import { UiItemContext } from './UiItemContext';
import { currentHelpers } from '../../redirection/current';
import { getSwiperContentInit } from '../reducers/reducerHelpers/swipeHelpers';
import SemUI from 'global/pageItem/SemUI';
import { uniqueKey } from 'global/common/keys';

/** Returns pageItems in a swipable format depending on the `swipeContentType`
 * @swipeContentTypes (pageAreaContent, groupedItems, normalItems)
 * @pageAreaContent pageItemsActive
 * @groupedItems groupDataProps
 * @normalItems swipeItems
 */
export const SwiperContext = createContext();

/** Returns pageItems in a swipable format depending on the `swipeContentType`
 * @swipeContentTypes (pageAreaContent, groupedItems, normalItems)
 * @pageAreaContent pageItemsActive
 * @groupedItems groupDataProps
 * @normalItems swipeItems
 */
const SwiperProvider = (props) => {

  const {
    swipeItems,
    uiElems,
    swipeContentType,
    fn_getContent,
    layoutType,
    isAdminHidden
  } = props

  // parentContext
  const parentContext = useContext(ParentContext)
  const { states } = parentContext ?? {}
  const { currentSettings_state, paps_state } = states
  const { aps_global } = _.get(currentSettings_state, 'gvviSettings', {});
  const { formatting, projectOptions } = aps_global ?? {}

  // papsContext 
  const { view } = paps_state

  // frameworkContext
  const frameworkContext = useContext(FrameworkContext);
  const { framework_state } = frameworkContext ?? {}
  const { desktopMode } = framework_state ?? {}

  // pageAreaContext
  const pageAreaContext = useContext(PageAreaContext)
  const { pageArea_state } = pageAreaContext ?? {}
  const { pageData, pageItemsActive, profileItems, isAppUserPage } = pageArea_state ?? {}

  // uiItemContext
  const uiItemContext = useContext(UiItemContext)
  const { item_state } = uiItemContext ?? {}
  const { viewItem_key } = item_state ?? {}

  // filterContext
  const filterContext = useContext(FilterContext);
  const { uiFilter_state } = filterContext ?? {}
  const { groupDataProps, viewItem } = uiFilter_state ?? {}
  const { display, grouping: grouping_viewItem } = viewItem ?? {}
  const { defaultToToday, groupType, useStartCaseHeaders } = grouping_viewItem ?? {}
  const { defaultTab } = groupDataProps ?? {}
  const { showTabCount } = display ?? {}

  const _viewItem = viewItem_key ? viewItem_key : view

  const _ui_elems = useMemo(() => {
    const { groups } = groupDataProps ?? {}
    if (!groups) return;
    const elems = _.map(groups, (group) => ({
      key: group.key,
      content: <SemUI key={uniqueKey('sigi', group.key)} selectedGroupItem={group.key} />
    }))
    return elems
  }, [groupDataProps])

  const initInfo = useMemo(() => {

    let _currentSwipeItems;

    switch (swipeContentType) {
      // the data in coming from pageItemsActive
      case gEnums.swipeContentTypes.pageAreaContent:
        _currentSwipeItems = pageItemsActive
        break;
      // the data in coming from groupedItems
      case gEnums.swipeContentTypes.groupedItems:
        _currentSwipeItems = groupDataProps
        break;
      case gEnums.swipeContentTypes.normalItems:
        _currentSwipeItems = swipeItems
        break;
      default:
    }

    const isBack = currentHelpers.storageItem_get('isBack')
    let initTabIndex = 0

    if (isBack) {
      let initIndex;
      switch (swipeContentType) {
        case gEnums.swipeContentTypes.groupedItems:
          initIndex = currentHelpers.storageItem_get('tab-container-' + _viewItem)
          break;
        default:
          initIndex = currentHelpers.storageItem_get('tab-' + _viewItem)
      }
      initTabIndex = initIndex ? initIndex : 0
    }

    if (defaultToToday && defaultTab && defaultTab.value && _currentSwipeItems && _currentSwipeItems.groups) {
      const dt = _.findKey(_currentSwipeItems.groups, function (i) { return (i.key && i.key === defaultTab.value); });
      if (dt) { initTabIndex = parseInt(dt) }
    }

    return { initTabIndex, currentSwipeItems: _currentSwipeItems, uiElems: _ui_elems }

  }, [viewItem_key, pageItemsActive, groupDataProps, swipeItems, swipeContentType])

  // local state
  const init_state = { desktopMode, formatting, projectOptions, groupType, useStartCaseHeaders, fn_getContent, layoutType, isAdminHidden }

  const [swiper_state, swiper_dispatch] = useReducer(swiperReducer, swiperInitialState(init_state));
  const { swiperContent, tabIndex } = swiper_state ?? {}

  const swiper_handlers = useMemo(
    () => swiperHandlers(swiper_dispatch),
    [swiper_dispatch]
  );

  const swiper_fns = { fn_getContent }

  // after initInfo, get the swiperInit
  const swiperInit = useMemo(() => {
    const { initTabIndex, currentSwipeItems } = initInfo
    if (currentSwipeItems) {
      const gscProps = { swipeContentType, tabIndex: initTabIndex ? initTabIndex : 0, view, _viewItem, currentSwipeItems, profileItems, showTabCount, group_viewItem: grouping_viewItem, isAppUserPage, uiElems }
      const _swiperInit = getSwiperContentInit(init_state, gscProps)
      return _swiperInit
    }
  }, [initInfo])

  useEffect(() => {
    // if swiperInit updates...
    if (swiperInit) {
      swiper_handlers.handleSet_swiperContent(swiperInit.swiperContent, swiperInit.gscProps, pageData)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [swiperInit, pageData]);

  if (swiperContent) {
    return <SwiperContext.Provider value={{ ...props, swiper_state, swiper_handlers, swiper_fns }}>
      {props.children}
    </SwiperContext.Provider>
  }

  return <div></div>

};

export default SwiperProvider